import React, { useState, useEffect } from 'react';
import { Form, Table, Modal, Button, Spinner } from "react-bootstrap";
import Trainer from "../images/Trainer.png";
import Student from "../images/Student.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield } from '@fortawesome/free-solid-svg-icons';
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import authService from '../../../utils/authService';

interface Course {
  course_id: string;
  course_name: string;
}

interface College {
  college_id: string;
  college_name: string;
}

interface Batch {
  course_id: string;
  batch_name: string;
  batch_id?: string;
  delivery_type: string;
  max_no_of_students: number;
  start_date: string;
  indicative_date: string;
  college_id?: string;
  del_row?: boolean;
  is_testing_batch?: boolean;
  allow_student_profile_edit?: boolean | null;
  electron_app?: boolean;
}

const BatchCreation: React.FC = () => {
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [colleges, setColleges] = useState<College[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [modalType, setModalType] = useState<string>("new");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<Batch>({
    course_id: "",
    batch_name: "",
    delivery_type: "Online",
    max_no_of_students: 0,
    start_date: "",
    indicative_date: "",
    college_id: "",
    is_testing_batch: false,
    allow_student_profile_edit: false,
    electron_app: true,
  });
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCourseModal, setShowCourseModal] = useState<boolean>(false);
const [selectedCourse, setSelectedCourse] = useState("");
const navigate= useNavigate();
  useEffect(() => {
    fetchCourses();
    fetchColleges();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await authService.get('admin/batch/get_all_courses_for_batch/');
      setCourses(response.data.courses);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching courses:', error);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await authService.get('admin/colleges/');
      setColleges(response.data.colleges || []);
    } catch (error) {
      console.error('Error fetching colleges:', error);
    }
  };
  
useEffect(() => {
    if (selectedCourse!="") {
        fetchBatches();
    }
},[selectedCourse])

  const fetchBatches = async () => {
    try {
      const response = await authService.get(`admin/batch/get_all_batches/${selectedCourse}/`);
      setBatches(response.data.batches);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleDelete = (batch: Batch): void => {
    setSelectedBatch(batch);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedBatch) {
      try {
        const json = {
          batch_id: selectedBatch.batch_id,
        };
  
        const response = await authService.post("admin/batch/delete_batch/", json);
  
        fetchBatches();
        setShowDeleteModal(false);
      } catch (error) {
        console.error("Error deleting batch:", error);
      }
    }
  };

  const handleModal = (type: string, batch?: Batch): void => {
    setSelectedBatch(batch?batch:null)
    setModalType(type);
    if (type === "new") {
      setFormData({ course_id: selectedCourse, batch_name: "", delivery_type: "Online", max_no_of_students: 0, start_date: "", indicative_date: "", college_id: "", is_testing_batch: false, allow_student_profile_edit: false, electron_app: true });
    } else if (batch) {
      // Format dates for the form inputs
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        
        // Handle different date formats
        let date: Date;
        
        // If it's already in YYYY-MM-DD format, use it directly
        if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
          return dateString.split('T')[0]; // Remove time part if present
        }
        
        // If it's in ISO format with time
        if (dateString.includes('T')) {
          date = new Date(dateString);
        } else {
          // Try parsing as different formats
          const parts = dateString.split(/[-/]/);
          if (parts.length === 3) {
            // Assume YYYY-MM-DD or MM/DD/YYYY format
            if (parts[0].length === 4) {
              // YYYY-MM-DD format
              date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            } else {
              // MM/DD/YYYY format
              date = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
            }
          } else {
            date = new Date(dateString);
          }
        }
        
        if (isNaN(date.getTime())) {
          return "";
        }
        
        const formatted = date.toISOString().split('T')[0];
        return formatted;
      };

      const formattedBatch = {
        ...batch,
        course_id: selectedCourse, // Use the currently selected course
        start_date: formatDateForInput(batch.start_date),
        indicative_date: formatDateForInput(batch.indicative_date),
        college_id: batch.college_id || "",
        is_testing_batch: batch.is_testing_batch || false,
        electron_app: batch.electron_app !== false,
      };
      setFormData(formattedBatch);
    }
    setShowModal(true);
    setError("");
  };

  const handleSave = async (): Promise<void> => {
    if (!formData.batch_name || !formData.indicative_date || !formData.start_date || !formData.college_id) {
        setError("All fields are required");
        return;
    }

    const isBatchTaken = batches.some(batch => (batch.batch_name.toLowerCase() === formData.batch_name.toLowerCase() && batch.batch_id !== formData.batch_id));
    if (isBatchTaken) {
        setError("Batch name not available");
        return;
    }

    const startDate = new Date(formData.start_date);
    const indicativeDate = new Date(formData.indicative_date);

    if (startDate >= indicativeDate) {
        setError("Start date must be before indicative date");
        return;
    }

    if (formData.max_no_of_students < 0) {
        setError("Max number of students cannot be negative");
        return;
    }
    if (modalType === "edit" && selectedBatch) {
        const currentStudentCount = batches.find(batch => batch.batch_id === selectedBatch.batch_id)?.max_no_of_students || 0;
        if (formData.max_no_of_students < currentStudentCount) {
            setError(`Max number of students cannot be less than the current number of students (${currentStudentCount})`);
            return;
        }
    }

    setLoading(true);
    setError("");

    try {
        // Create payload without del_row field
        const { del_row, ...payloadWithoutDelRow } = formData;
        const json = {
            ...payloadWithoutDelRow,
            course_id: formData.course_id, // Use the course selected in the modal dropdown
            college_id: formData.college_id,
            allow_student_profile_edit: formData.allow_student_profile_edit ?? false,
            electron_app: formData.electron_app !== false,
        };

        const response = await authService.post("admin/batch/create_batch/", json);
        
        fetchBatches();
        setShowModal(false);
        setLoading(false);
    } catch (error) {
        setError("Error saving batch");
        console.error("Error saving batch:", error);
    } finally {
        setLoading(false);
    }
};

  return (
    <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
      <div className="row mb-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label htmlFor="courseSelect" className="form-label fw-bold">Course</label>
                  <Form.Select 
                    id="courseSelect"
                    onChange={(e) => setSelectedCourse(e.target.value)} 
                    value={selectedCourse}
                  >
                    <option value="">Choose course...</option> 
                    {courses.map((course) => (
                      <option 
                        key={course.course_id} 
                        value={course.course_id}
                      >
                        {course.course_name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="col-md-4">
                  <label htmlFor="batchSearch" className="form-label fw-bold">Search Batch</label>
                  <Form.Control
                    id="batchSearch"
                    type="text"
                    placeholder="Enter batch name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="col-md-4 text-end">
                  <button className="btn btn-primary" onClick={() => { handleModal("new") }}>
                    <i className="bi bi-plus-circle me-1"></i>
                    New Batch
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4">

      <Table className='border-top mt-2'>
        <thead  style={{borderBottom:'1px solid black',borderTop:'1px solid black'}}>
          <tr>
            <td className='fw-normal'>Sl No</td>
            <td className='fw-normal'>Batch Name</td>
          {/*  <td className='fw-normal'>Batch Id</td>  */}
            <td className='fw-normal'>Max No of Students</td>
            <td className='fw-normal'>Start Date</td>
            <td className='fw-normal'>End Date</td>
            <td className='fw-normal'>Delivery Type</td>
            <td className='fw-normal'>Testing Batch</td>
            <td className='fw-normal'>Student profile edit</td>
            <td className='fw-normal'>Desktop app required</td>
            <td className='fw-normal'>Actions</td>
          </tr>
        </thead>
        <tbody>
          {batches
            .filter((batch) => !batch.del_row && batch.batch_name.toLowerCase().includes(search.toLowerCase()))
            .map((batch, index) => (
              <tr key={index + 1}>
                <td>{index + 1}</td>
                <td>{batch.batch_name}</td>
               {/* <td>{batch.batch_id}</td>  */}
                <td>{String(batch.max_no_of_students)}</td>
                <td>{batch.start_date ? new Date(batch.start_date).toLocaleDateString() : '--'}</td>
                <td>{batch.indicative_date ? new Date(batch.indicative_date).toLocaleDateString() : '--'}</td>
                <td>{batch.delivery_type}</td>
                <td>
                  <span>
                    {batch.is_testing_batch ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span>
                    {batch.allow_student_profile_edit ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span>
                    {batch.electron_app !== false ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <FaEdit className="fs-6 me-2" title="Edit" onClick={() => { handleModal("edit", batch) }} role="button" style={{ cursor: "pointer" }} />
                  <FaTrash className="fs-6 me-2" title="Delete" role="button" onClick={() => handleDelete(batch)} style={{ cursor: "pointer" }} />
                  <img src ={Trainer} title="Trainer"  alt='Trainer' role ="button"  aria-label="Trainer"  onClick={() => {
                      navigate('/admin/batches/user', { state: { batch_id: batch.batch_id, course_id:selectedCourse , batch_name: batch.batch_name } });
                    }}
                  />
                  <img src= {Student} title="Student" alt='Student' role ="button"  sizes='20'  aria-label="Student" onClick={() => {
                      navigate('/admin/batches/student', { state: { batch_id: batch.batch_id, course_id:selectedCourse , batch_name: batch.batch_name } });
                    }} />
                  <span role="button" title="Coordinator" aria-label="Coordinator" className="ms-2" onClick={() => {
                      navigate('/admin/batches/coordinator', { state: { batch_id: batch.batch_id, course_id:selectedCourse , batch_name: batch.batch_name } });
                    }}>
                    <FontAwesomeIcon icon={faUserShield} />
                  </span>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalType === "new" ? "Add New Batch" : "Edit Batch"}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <p className="text-danger">{error}</p>}
          <Form>

            <Form.Group controlId="formCourse" className="d-flex align-items-center justify-content-between">
              <Form.Label>Course</Form.Label>
              <Form.Select 
                style={{
                  width: "50%",
                  maxHeight: "150px",
                  overflowY: "auto"
                }} 
                name="course_id" 
                disabled={modalType === "edit"} 
                value={formData.course_id} 
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option 
                    key={course.course_id} 
                    value={course.course_id}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {course.course_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="formCollege" className="d-flex align-items-center justify-content-between mt-3">
              <Form.Label>College</Form.Label>
              <Form.Select 
                style={{
                  width: "50%",
                  maxHeight: "150px",
                  overflowY: "auto"
                }} 
                name="college_id" 
                value={formData.college_id || ""} 
                onChange={(e) => setFormData({ ...formData, college_id: e.target.value })}
              >
                <option value="">Select College</option>
                {colleges.map((college) => (
                  <option 
                    key={college.college_id} 
                    value={college.college_id}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {college.college_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="formBatchName" className=" mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Batch Name</Form.Label>
              <Form.Control style={{
      width: "50%"
    }}
                type="text"
                name="batch_name"
                value={formData.batch_name}
                onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                // placeholder="Enter Batch Name"
              />
            </Form.Group>

            <Form.Group controlId="formDeliveryType"  className=" mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Delivery Type</Form.Label>
              <Form.Select style={{
      width: "50%"
    }} name="delivery_type" value={formData.delivery_type} onChange={(e) => setFormData({ ...formData, delivery_type: e.target.value })}>
                <option value="Online">Online</option>
                <option value="In-person">In-person</option>
                <option value="Hybrid">Hybrid</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="formMaxNoOfStudents"  className=" mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Max No of students</Form.Label>
              <Form.Control
                type="number"
                name="max_no_of_students"
                value={formData.max_no_of_students || ""}
                min={0}
                style={{
                  width: "50%"
                }}
                onChange={(e) => setFormData({
                  ...formData,
                  max_no_of_students: e.target.value === "" ? 0 : Number(e.target.value)
                })}
                // placeholder="Enter Max No of students"
              />
            </Form.Group>

            <Form.Group  className=" mt-3 d-flex align-items-center justify-content-between"controlId="formStartDate">
              <Form.Label>Enter Start Date</Form.Label>
              <Form.Control
                type="date"
                name="start_date"
                style={{
                  width: "50%"
                }}
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                // placeholder="Enter Start Date"
              />
            </Form.Group>

            <Form.Group controlId="formIndicativeDate"  className=" mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Enter Indicative Date</Form.Label>
              <Form.Control
              style={{
                width: "50%"
              }}
                type="date"
                name="indicative_date"
                value={formData.indicative_date}
                onChange={(e) => setFormData({ ...formData, indicative_date: e.target.value })}
                // placeholder="Enter Indicative Date"
              />
            </Form.Group>

            <Form.Group controlId="formIsTestingBatch" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Testing Batch</Form.Label>
              <div style={{ width: "50%" }}>
                <Form.Check
                  type="checkbox"
                  name="is_testing_batch"
                  checked={formData.is_testing_batch || false}
                  onChange={(e) => setFormData({ ...formData, is_testing_batch: e.target.checked })}
                  label="Is Testing Batch"
                />
              </div>
            </Form.Group>

            <Form.Group controlId="formAllowStudentProfileEdit" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Allow student profile edit</Form.Label>
              <div style={{ width: "50%" }}>
                <Form.Check
                  type="checkbox"
                  name="allow_student_profile_edit"
                  checked={formData.allow_student_profile_edit ?? false}
                  onChange={(e) => setFormData({ ...formData, allow_student_profile_edit: e.target.checked })}
                  label="Students can edit their profile"
                />
              </div>
            </Form.Group>

            <Form.Group controlId="formElectronApp" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Require desktop app</Form.Label>
              <div style={{ width: "50%" }}>
                <Form.Check
                  type="checkbox"
                  name="electron_app"
                  checked={formData.electron_app !== false}
                  onChange={(e) => setFormData({ ...formData, electron_app: e.target.checked })}
                  label="Students must take tests in the desktop app"
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the batch: <strong>{selectedBatch?.batch_name}</strong>?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Course Selection Modal */}
      {showCourseModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Course</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCourseModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="list-group">
                  <button
                    className={`list-group-item list-group-item-action ${
                      selectedCourse === "" ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedCourse("");
                      setBatches([]);
                      setShowCourseModal(false);
                    }}
                  >
                    Choose course...
                  </button>
                  {courses.map((course) => (
                    <button
                      key={course.course_id}
                      className={`list-group-item list-group-item-action ${
                        selectedCourse === course.course_id ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedCourse(course.course_id);
                        setBatches([]);
                        setShowCourseModal(false);
                      }}
                    >
                      {course.course_name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCourseModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchCreation;
